import type { JSX } from "react";
import {
    AtomContainer,
    AtomContainerQuery,
    IAtomModel,
} from "@alchemiesolns/atomcontainer";
import {
    calculateWidth,
    getAnchor,
    getAtomSymbolWithImplicitHydrogens,
    getOffsetX,
    calculateGroupOffset,
} from "@alchemiesolns/line-angle-util";
import { getAtomColor } from "@alchemiesolns/svg-format";
import { VisibilityConfigDef, VisibilityFlag } from "@alchemiesolns/visibility-config";
import { getFormat } from "./utils/formatContext";

export const svgPaperAtoms = {
    clapSymbolProps: (
        a: IAtomModel,
        container: AtomContainer,
        visibleAtoms?: IAtomModel[]
    ) => {
        const { symbol } =
            getAtomSymbolWithImplicitHydrogens(
                a,
                container,
                visibleAtoms ?? container.atoms,
                true
            );
        // Note: getAnchor API simplified - extra params ignored
        const anchor = getAnchor(symbol);

        return {
            symbol,
            anchor,
        };
    },
    calcSymbolSize: (symbolText: string, _anchor: string) => {
        // Note: getOffsetX and calculateWidth APIs simplified - extra params ignored
        const offset = getOffsetX(symbolText);
        const width = calculateWidth(symbolText);
        return {
            offset,
            width,
        };
    },
    calcHydrogenDirection: (
        a: IAtomModel,
        container: AtomContainer,
        visibleAtoms?: IAtomModel[]
    ) => {
        // Check if there are H atoms attached to this atom that are NOT in visibleAtoms
        // This includes both implicit H atoms and explicit H atoms that are hidden
        const allBonds = container.getBondsAttachedTo(a);
        const hBonds = allBonds.filter(b => {
            const neighbor = b.a1 === a ? b.a2 : b.a1;
            return neighbor.symbol === 'H';
        });

        const allAtoms = visibleAtoms ?? container.atoms;
        const hiddenHBonds = hBonds.filter(b => {
            const neighbor = b.a1 === a ? b.a2 : b.a1;
            return !allAtoms.includes(neighbor);
        });

        // Also check for implicit H atoms
        const implicitHs = AtomContainerQuery.getImplicitHydrogensAttachedTo(a, container);
        const hiddenImplicitHs = implicitHs.filter(h => !allAtoms.includes(h));

        // If no hidden hydrogens (explicit or implicit), don't return a direction
        if (hiddenHBonds.length === 0 && hiddenImplicitHs.length === 0) {
            return undefined;
        }

        // Get the display symbol to determine direction
        const { symbol } = svgPaperAtoms.clapSymbolProps(
            a,
            container,
            visibleAtoms
        );
        const isPaperCarbon =
            a.symbol === "C"
                ? svgPaperAtoms.isPaperCarbon(a, container, visibleAtoms)
                : false;

        if (!isPaperCarbon) {
            if (symbol[0] === "H") {
                // Symbol starts with H (like "H₂O", "HCl") - hydrogen on LEFT
                return [-1, 0, 0];
            } else if (
                symbol.length > 1 &&
                (symbol[symbol.length - 1] === "H" ||
                    symbol[symbol.length - 2] === "H")
            ) {
                // Symbol ends with H (like "OH", "CH₃") - hydrogen on RIGHT
                return [1, 0, 0];
            }
        }
        return undefined;
    },
    /**
     * Renders a paper carbon (implicit carbon shown as a small dot/circle).
     * Adds required data attributes for drag-to-connect functionality:
     * - id: atom.id
     * - data-atom-id: atom.id (for element identification)
     * - data-atom-index: index in container.atoms array
     * - data-element: atom.symbol (chemical element symbol)
     * - data-x, data-y: atom position coordinates (stringified)
     * - data-visibility: "visible" | "hidden" | "hidden-lp"
     *
     * @param a - The atom model to render
     * @param container - The atom container holding all atoms and bonds
     * @param visDefs - Visibility configuration array
     * @returns JSX element for the paper carbon or null if no line bonds attached
     */
    drawPaperCarbon: (a: IAtomModel, container: AtomContainer, visDefs: VisibilityConfigDef[]) => {
        const attBonds = container.getBondsAttachedTo(a);
        const lineBonds = attBonds.filter(
            (b) => b.type === "l" && b.a1.symbol !== "H" && b.a2.symbol !== "H"
        );

        if (lineBonds.length > 0) {
            // Look up atom index in container
            const atomIndex = container.atoms.findIndex(atom => atom.id === a.id);

            // Look up visibility flag for this atom
            // Note: HiddenLP only hides lone pairs, not the atom itself
            const visDef = visDefs.find(v => v.a === atomIndex);
            const visibilityAttr = visDef?.visibilityFlag === VisibilityFlag.Hidden ? "hidden" : "visible";

            return (
                <g className="svg-atom-group">
                    <circle
                        id={a.id}
                        className="svg-atom"
                        data-atom-id={a.id}
                        data-atom-index={String(atomIndex)}
                        data-element={a.symbol}
                        data-x={String(a.pos!.x)}
                        data-y={String(a.pos!.y)}
                        data-visibility={visibilityAttr}
                        cx={0}
                        cy={0}
                        r={getFormat().atom.radius * getFormat().atom.carbon.scale}
                        fill={getAtomColor(a.symbol)}
                        opacity={1}
                    />
                </g>
            );
        } else {
            return (
                <>
                </>
            );
        }
    },
    isPaperCarbon: (
        a: IAtomModel,
        container: AtomContainer,
        visibleAtoms?: IAtomModel[]
    ) => {
        const { symbol } = svgPaperAtoms.clapSymbolProps(
            a,
            container,
            visibleAtoms
        );
        const attBonds = container.getBondsAttachedTo(a);
        const attachedHs = AtomContainerQuery.getImplicitHydrogensAttachedTo(
            a,
            container
        );
        return (
            ((symbol[0] === "C" && (!symbol[1] || symbol[1] === "H")) ||
                symbol[symbol.length - 1] === "C") &&
            (attBonds.length !== attachedHs.length || symbol.length === 1)
        );
    },
    /**
     * Renders an atom element (either as text with symbol or as paper carbon circle).
     * Adds required data attributes for drag-to-connect functionality:
     * - id: atom.id
     * - data-atom-id: atom.id (for element identification)
     * - data-atom-index: index in container.atoms array
     * - data-element: atom.symbol (chemical element symbol)
     * - data-x, data-y: atom position coordinates (stringified)
     * - data-visibility: "visible" | "hidden" | "hidden-lp"
     *
     * Paper carbons are rendered as circles, other atoms as text elements.
     * All attributes follow the schema defined in specs/002-data-in-svg/data-model.md
     *
     * @param a - The atom model to render
     * @param container - The atom container holding all atoms and bonds
     * @param visibleAtoms - Optional array of visible atoms for layout calculations
     * @param visDefs - Visibility configuration array
     * @returns JSX element for the atom (text or circle)
     */
    drawAtom: (
        a: IAtomModel,
        container: AtomContainer,
        visibleAtoms: IAtomModel[],
        visDefs: VisibilityConfigDef[]
    ) => {
        const { symbol, anchor: _anchor } = svgPaperAtoms.clapSymbolProps(
            a,
            container,
            visibleAtoms
        );

        // Calculate offset for proper centering/alignment
        // Check if this is a group (multi-character non-element symbol)
        const commonElements = ['Cl', 'Br', 'Na', 'Mg', 'Ca', 'Al', 'Si', 'Fe', 'Cu', 'Zn', 'Ag', 'Sn', 'Pb'];
        const isGroup = !symbol.includes('H') && (
            symbol.length > 2 ||
            (symbol.length === 2 && !commonElements.includes(symbol))
        );

        let hydrogenOffset = 0;
        if (isGroup) {
            // Determine bond direction for group alignment
            const bonds = container.getBondsAttachedTo(a);
            let bondDirection: 'left' | 'right' | 'default' = 'default';

            if (bonds.length > 0) {
                // Get the first bond's neighbor position
                const bond = bonds[0];
                const neighbor = bond.a1 === a ? bond.a2 : bond.a1;
                const neighborX = neighbor.pos?.x ?? 0;
                const atomX = a.pos?.x ?? 0;

                // Determine bond direction
                bondDirection = neighborX < atomX ? 'left' : 'right';
            }

            hydrogenOffset = calculateGroupOffset(symbol, bondDirection, getFormat().fontSize);
        } else {
            // Regular hydrogen offset for non-group atoms
            hydrogenOffset = getOffsetX(symbol, getFormat().fontSize);
        }

        // Look up atom index and visibility flag
        const atomIndex = container.atoms.findIndex(atom => atom.id === a.id);
        const visDef = visDefs.find(v => v.a === atomIndex);

        // Check if this should be rendered as paper carbon
        // Override if ForceText or ForceTextHiddenLP flag is set
        const isPaperCarbon = svgPaperAtoms.isPaperCarbon(a, container, visibleAtoms) &&
                              visDef?.visibilityFlag !== VisibilityFlag.ForceText &&
                              visDef?.visibilityFlag !== VisibilityFlag.ForceTextHiddenLP;

        if (isPaperCarbon) {
            return svgPaperAtoms.drawPaperCarbon(a, container, visDefs);
        } else {
            // Look up visibility attribute for text rendering
            // Note: HiddenLP only hides lone pairs, not the atom itself
            const visibilityAttr = visDef?.visibilityFlag === VisibilityFlag.Hidden ? "hidden" : "visible";

            return (
                <g className={"svg-atom-group"}>
                    <text
                        id={a.id}
                        className="svg-atom svg-text"
                        data-atom-id={a.id}
                        data-atom-index={String(atomIndex)}
                        data-element={a.symbol}
                        data-x={String(a.pos!.x)}
                        data-y={String(a.pos!.y)}
                        data-visibility={visibilityAttr}
                        x={getFormat().atom.symbolOffset[0] + hydrogenOffset}
                        y={getFormat().atom.symbolOffset[1]}
                        fontSize={getFormat().fontSize + "px"}
                        fontFamily={getFormat().fontFamily}
                        fill={getAtomColor(a.symbol)}
                        textAnchor="middle"
                        dominantBaseline="central"
                    >
                        {symbol}
                    </text>
                </g>
            );
        }
    },
    drawAtoms: (atoms: IAtomModel[], container: AtomContainer, visDefs: VisibilityConfigDef[]): JSX.Element[] => {
        return atoms.map((a) => svgPaperAtoms.drawAtom(a, container, atoms, visDefs));
    },
};
